import styled from "styled-components";

type BackgroundType = { background: string };
type ColorType = { color: string };
type BorderType = { background: string; border: string };
type ButtonType = { background: string; color: string };
type OutlineButtonType = { border: string; color: string };

export const Container = styled.div<BackgroundType>`
    display: flex;
    flex-direction: column;
    background: ${(props) => props.background};
    border-radius: 8px;
    overflow: hidden;
`;

export const Body = styled.div`
    display: flex;
    flex-direction: column;
    padding: 24px 24px 0 24px;
    gap: 16px;
`;

export const TitleRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
`;

export const IconBox = styled.div<BorderType>`
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: ${(props) => props.background};
    border: 1px solid ${(props) => props.border};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

export const TitleGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

export const Title = styled.span<ColorType>`
    font-size: 18px;
    font-weight: 700;
    color: ${(props) => props.color};
    line-height: 1.3;
`;

export const Subtitle = styled.span<ColorType>`
    font-size: 13px;
    color: ${(props) => props.color};
    line-height: 1.4;
`;

export const FeaturesCard = styled.div<BorderType>`
    background: ${(props) => props.background};
    border: 1px solid ${(props) => props.border};
    border-radius: 10px;
    padding: 16px;
`;

export const FeaturesTitle = styled.span<ColorType>`
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => props.color};
    margin-bottom: 12px;
    display: block;
`;

export const FeaturesGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
`;

export const FeatureItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const FeatureIconRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 4px;
`;

export const FeatureName = styled.span<ColorType>`
    font-size: 12px;
    font-weight: 600;
    color: ${(props) => props.color};
    line-height: 1.3;
`;

export const FeatureDesc = styled.span<ColorType>`
    font-size: 11px;
    color: ${(props) => props.color};
    line-height: 1.4;
`;

export const StatusCard = styled.div<BorderType>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    background: ${(props) => props.background};
    border: 1px solid ${(props) => props.border};
    border-radius: 10px;
    padding: 12px 16px;
`;

export const StatusIconBox = styled.div<BorderType>`
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: ${(props) => props.background};
    border: 1px solid ${(props) => props.border};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

export const StatusText = styled.span<ColorType>`
    font-size: 13px;
    font-weight: 500;
    color: ${(props) => props.color};
`;

export const StatusBadge = styled.span<{ background: string; color: string }>`
    font-size: 11px;
    font-weight: 600;
    color: ${(props) => props.color};
    background: ${(props) => props.background};
    border-radius: 20px;
    padding: 2px 10px;
`;

export const StatusRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

export const StatusHint = styled.span<ColorType>`
    font-size: 12px;
    color: ${(props) => props.color};
`;

export const BenefitCard = styled.div<BorderType>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    background: ${(props) => props.background};
    border: 1px solid ${(props) => props.border};
    border-radius: 10px;
    padding: 12px 16px;
`;

export const BenefitIconBox = styled.div<BorderType>`
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: ${(props) => props.background};
    border: 1px solid ${(props) => props.border};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

export const BenefitTextGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

export const BenefitTitle = styled.span<ColorType>`
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => props.color};
`;

export const BenefitDesc = styled.span<ColorType>`
    font-size: 12px;
    color: ${(props) => props.color};
`;

export const Footer = styled.div<BackgroundType>`
    display: flex;
    flex-direction: column;
    padding: 16px 24px 20px 24px;
    gap: 8px;
    background: ${(props) => props.background};
`;

export const FooterButtons = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
`;

export const PrimaryButton = styled.button<ButtonType>`
    flex: 1;
    height: 44px;
    border-radius: 8px;
    border: none;
    background: ${(props) => props.background};
    color: ${(props) => props.color};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: filter 0.15s;

    &:hover {
        filter: brightness(0.9);
    }
`;

export const SecondaryButton = styled.button<OutlineButtonType>`
    flex: 1;
    height: 44px;
    border-radius: 8px;
    border: 1.5px solid ${(props) => props.border};
    background: transparent;
    color: ${(props) => props.color};
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: filter 0.15s;

    &:hover {
        filter: brightness(0.85);
    }
`;

export const FooterNote = styled.span<ColorType>`
    font-size: 12px;
    color: ${(props) => props.color};
    text-align: center;
`;

export const CloseButton = styled.button<{ color: string; background: string }>`
    position: absolute;
    top: 14px;
    right: 14px;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: none;
    background: ${(props) => props.background};
    color: ${(props) => props.color};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: filter 0.15s;

    &:hover {
        filter: brightness(0.85);
    }
`;

export const HeaderWrapper = styled.div`
    position: relative;
    padding: 20px 24px 0 24px;
`;
